import React, { FormEvent } from 'react';
import { Card, Flex, Box, Text, TextField, Button } from '@radix-ui/themes';
import { numberInputSchema } from '@/state/metricTypeValidation';
import { db } from '@/database/client';
import { useMetricFormStore } from '@/state/useMetricFormStore';
import { CheckCircledIcon } from '@radix-ui/react-icons';

import { MetricInputProps } from '@/components/Form/types';

export function NumberMetricInput({
  definition,
  onSaveSuccess,
}: MetricInputProps) {
  const { id: metricId, icon, name, unit } = definition;

  // Read ephemeral draft and error state from Zustand
  const draftValue = useMetricFormStore((s) => s.draftValues[metricId] ?? '');
  const error = useMetricFormStore((s) => s.errors[metricId] ?? null);
  const { setDraftValue, setError, clearDraft } = useMetricFormStore();

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent native page reload on Enter or Button click
    if (!draftValue) return;

    // 1. Execute Zod Validation
    const result = numberInputSchema.safeParse(draftValue);

    if (!result.success) {
      const errorMessage = result.error.issues[0]?.message || 'Invalid number';
      setError(metricId, errorMessage);
      return;
    }

    const validNumber = result.data;

    try {
      // 2. Write validated number to Dexie IndexedDB
      const today = new Date().toISOString().split('T')[0];
      await db.metricLogs.add({
        id: `log_${crypto.randomUUID()}`,
        metricId: metricId,
        value: validNumber,
        timestamp: Date.now(),
      });

      // 3. Clean up ephemeral store on success
      clearDraft(metricId);
      onSaveSuccess?.(validNumber);
    } catch (err) {
      console.error('Failed to commit log to IndexedDB:', err);
      setError(metricId, 'Database error. Could not save.');
    }
  };

  return (
    <Card
      size="2"
      style={{
        backgroundColor: 'var(--sky-dawn, #F9F6F0)',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Native form wrapper enables automatic Enter-key submission */}
      <form onSubmit={handleFormSubmit}>
        <Flex direction="column" gap="2">
          {/* Metric Header */}
          <Flex justify="between" align="center">
            <Flex align="center" gap="2">
              {icon}
              <Text
                weight="bold"
                size="2"
                style={{ color: 'var(--liturgy-ink)' }}
              >
                {name}
              </Text>
            </Flex>
            {unit && (
              <Text size="1" color="gray" weight="medium">
                {unit}
              </Text>
            )}
          </Flex>

          {/* Input & Explicit Save Button Row */}
          <Flex gap="2" align="center">
            <Box style={{ flexGrow: 1 }}>
              <TextField.Root
                type="text"
                inputMode="decimal"
                placeholder="0.0"
                value={draftValue}
                onChange={(e) => setDraftValue(metricId, e.target.value)}
                // Note: onBlur and onKeyDown removed; reliance is strictly on onSubmit
                color={error ? 'red' : 'gray'}
                variant="surface"
                size="2"
              >
                {unit && (
                  <TextField.Slot side="right">
                    <Text size="1" color="gray">
                      {unit}
                    </Text>
                  </TextField.Slot>
                )}
              </TextField.Root>
            </Box>

            {/* Explicit Commit Trigger */}
            <Button
              type="submit"
              size="2"
              variant="soft"
              disabled={!draftValue}
              style={{ cursor: draftValue ? 'pointer' : 'default' }}
            >
              <CheckCircledIcon height={16} width={16} />
              Save
            </Button>
          </Flex>

          {/* Inline Error Feedback */}
          {error && (
            <Text size="1" color="red" weight="medium">
              {error}
            </Text>
          )}
        </Flex>
      </form>
    </Card>
  );
}
